import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { flushSync, createPortal } from 'react-dom';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { LIST_USERS, LIST_PROFILES } from '../../graphql/queries';
import {
  CREATE_USER,
  UPDATE_USER,
  BULK_IMPORT_USERS,
  DELETE_USER,
  ENABLE_USER,
  DISABLE_USER,
} from '../../graphql/mutations';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { USER_VALIDATIONS, validatePassword, getPasswordPolicyDescription, validateUser } from '../../utils/validationUtils';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  useTheme,
  Tooltip,
  Alert,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Password as PasswordIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon
} from '@mui/icons-material';

import { Profile, User } from '../../types/models';
import UserFormDialog from './components/UserFormDialog';
import { parseCsv, escapeCsvField, parseMultiValue } from '../../utils/csvUtils';
import PasswordFieldWithSideToggle from '../../components/common/PasswordFieldWithSideToggle';
import { canAccess } from '../../utils/permissions';
import UserTable from './components/UserTable';

const UserManagementPage: React.FC = () => {
  const theme = useTheme();
  const { currentUser } = useSelector((state: RootState) => state.user);

  // 日付フォーマット関数
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ユーザー情報を取得
  const { currentUser: loggedInUser } = useSelector((state: RootState) => state.user);
  const companyId = loggedInUser?.companyId || '';

  // 権限フラグ
  const permCsvDownload = canAccess(loggedInUser, 'userMgmt.view.canCsvDownload');
  const permCreateNew = canAccess(loggedInUser, 'userMgmt.edit.canCreateNew');
  const permCsvImport = canAccess(loggedInUser, 'userMgmt.edit.canCsvImport');
  const permBulkUpdate = canAccess(loggedInUser, 'userMgmt.edit.canBulkUpdate');
  const permUpdate = canAccess(loggedInUser, 'userMgmt.edit.canUpdate');
  const permDelete = canAccess(loggedInUser, 'userMgmt.edit.canDelete');
  const permChangePassword = canAccess(loggedInUser, 'userMgmt.edit.canChangePassword');

  // 表示件数
  const DISPLAY_INCREMENT = 1000;
  const CSV_IMPORT_MAX_ROWS = 10000;
  /** API 1リクエストあたりの上限（バックエンド bulkImportUsers と一致） */
  const CSV_IMPORT_CHUNK_SIZE = 1000;
  /** この件数以上を一度にテーブル描画するときはオーバーレイを先に出す（検索解除・大量ヒット時のフリーズ対策） */
  const HEAVY_LOCAL_FILTER_ROWS = 2000;

  // 状態管理
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const prevFilteredUsersLengthRef = useRef<number>(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBulkDeleteDialog, setOpenBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [batchStatusChangeLoading, setBatchStatusChangeLoading] = useState(false);
  /** 一括有効/無効オーバーレイの文言（ref だと再描画されず、Table の loading 置き換えと同時に見えなくなるため state で保持） */
  const [batchStatusOverlayMessage, setBatchStatusOverlayMessage] = useState('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  /** 一括実行の選択: '0'=有効, '1'=無効, 'delete'=削除 */
  const [batchActionValue, setBatchActionValue] = useState<'0' | '1' | 'delete'>('0');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [dialogErrorMessage, setDialogErrorMessage] = useState<string | null>(null);
  const [passwordData, setPasswordData] = useState<{
    newPassword: string;
    confirmPassword: string;
    forceChange: boolean;
  }>({
    newPassword: '',
    confirmPassword: '',
    forceChange: true
  });

  // GraphQLクエリを実行（fetchMore / refetch 時も networkStatus で取得中を判定）
  const { data, loading, networkStatus, error, fetchMore, refetch: refetchUsers } = useQuery(LIST_USERS, {
    variables: {
      companyId,
      limit: DISPLAY_INCREMENT,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !companyId, // companyIdがない場合はクエリを実行しない
  });

  const listFetchBusy =
    isAutoLoading ||
    loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch;

  /** 一覧APIの取得が終わってから、大量行の描画がコミットされるまでオーバーレイを維持 */
  const [postFetchPaintHold, setPostFetchPaintHold] = useState(false);
  /** メモリ上の全件に近い件数を再フィルタしてテーブルに載せる直前〜直後（検索条件の変更・解除など） */
  const [clientFilterPaintHold, setClientFilterPaintHold] = useState(false);
  const prevListFetchBusyRef = useRef(false);
  const filterEffectGenRef = useRef(0);
  /** 検索の「Clear」後に refetch 完了まで、古いキャッシュの listUsers を users に流し込まない */
  const [listHydrationPaused, setListHydrationPaused] = useState(false);

  useLayoutEffect(() => {
    if (listFetchBusy) {
      setPostFetchPaintHold(false);
      setClientFilterPaintHold(false);
    } else if (prevListFetchBusyRef.current) {
      setPostFetchPaintHold(true);
    }
    prevListFetchBusyRef.current = listFetchBusy;
  }, [listFetchBusy]);

  useLayoutEffect(() => {
    if (!postFetchPaintHold) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setPostFetchPaintHold(false);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [postFetchPaintHold, users, filteredUsers]);

  // 一覧取得・描画ホールド用（一括ステータス変更は別レイヤーで表示し、メッセージが上書きされないようにする）
  const showUserListOverlay =
    Boolean(companyId) &&
    !batchStatusChangeLoading &&
    (listFetchBusy || postFetchPaintHold || clientFilterPaintHold);

  // ミューテーションを設定
  const [createUser] = useMutation(CREATE_USER);
  const [updateUser] = useMutation(UPDATE_USER);
  const [bulkImportUsers] = useMutation(BULK_IMPORT_USERS);
  const [deleteUser] = useMutation(DELETE_USER);
  const [enableUser] = useMutation(ENABLE_USER);
  const [disableUser] = useMutation(DISABLE_USER);

  // プロファイル一覧（管理者ユーザー編集時の選択肢）
  const { data: profilesData } = useQuery(LIST_PROFILES, {
    variables: { companyId, limit: 100 },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
  });
  const profiles: Profile[] = useMemo(
    () => profilesData?.listProfiles?.items || [],
    [profilesData]
  );

  // 初回データ取得完了を追跡するためのref
  const initialDataLoadedRef = useRef<boolean>(false);
  
  // データが取得されたらusersステートを更新
  useEffect(() => {
    if (listHydrationPaused) return;
    if (data?.listUsers?.items) {
      // nullチェックを追加し、nullの場合はフィルタリング
      let convertedUsers = data.listUsers.items
        .filter((user: User | null): user is User => user !== null);

      // currentUser?.managementScopeに基づくフィルタリング
      if (currentUser?.managementScope && currentUser.managementScope.trim() !== '') {
        const scope = currentUser.managementScope.trim();
        convertedUsers = convertedUsers.filter((user: User) => {
          const depts = user.departments && user.departments.length > 0
            ? user.departments
            : (user.department ? [user.department] : []);
          return depts.includes(scope);
        });
      }

      setUsers(convertedUsers);
      setHasMore(!!data.listUsers.nextToken);

      // 初回データ取得が完了したことをマーク
      if (!initialDataLoadedRef.current) {
        initialDataLoadedRef.current = true;
        // 次のレンダリングサイクルでisInitialLoadをfalseにする
        // これにより、初回データ取得後の自動取得を防ぐ
        requestAnimationFrame(() => {
          setIsInitialLoad(false);
        });
      }
    }
  }, [data, currentUser?.managementScope, listHydrationPaused]);

  // 検索キーワードが変更されたときにフィルタリング（クライアントのみ。DBは listUsers のページングで取得）
  useEffect(() => {
    const nextFiltered =
      searchKeywords.length === 0
        ? users
        : users.filter((user: User) =>
            searchKeywords.some((keyword) => {
              const term = keyword.trim().toLowerCase();
              if (!term) return false;
              return (
                (user.displayName && user.displayName.toLowerCase().includes(term)) ||
                (user.givenUserId && user.givenUserId.toLowerCase().includes(term)) ||
                (user.departments && user.departments.length > 0
                  ? user.departments.some(d => d.toLowerCase().includes(term))
                  : (user.department ? user.department.toLowerCase().includes(term) : false))
              );
            })
          );

    const gen = ++filterEffectGenRef.current;
    const isCurrentGen = () => filterEffectGenRef.current === gen;

    if (nextFiltered.length < HEAVY_LOCAL_FILTER_ROWS) {
      setClientFilterPaintHold(false);
      setFilteredUsers(nextFiltered);
      return;
    }

    setClientFilterPaintHold(true);
    let raf1 = 0;
    let raf2 = 0;
    let raf3 = 0;
    raf1 = requestAnimationFrame(() => {
      if (!isCurrentGen()) return;
      setFilteredUsers(nextFiltered);
      raf2 = requestAnimationFrame(() => {
        if (!isCurrentGen()) return;
        raf3 = requestAnimationFrame(() => {
          if (isCurrentGen()) {
            setClientFilterPaintHold(false);
          }
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(raf3);
      if (filterEffectGenRef.current === gen) {
        setClientFilterPaintHold(false);
      }
    };
  }, [searchKeywords, users]);

  // フィルタリング結果がDISPLAY_INCREMENT未満の場合、自動的に追加取得
  const prevUsersLengthRef = useRef<number>(0);
  const lastAutoLoadTokenRef = useRef<string | null>(null);
  const isFirstAutoLoadCheckRef = useRef<boolean>(true);
  
  useEffect(() => {
    // 一覧取得・描画ホールド中、または次のページがない場合は何もしない
    if (listFetchBusy || postFetchPaintHold || clientFilterPaintHold || !hasMore) {
      prevUsersLengthRef.current = users.length;
      return;
    }

    // 初回データ取得が完了していない場合は何もしない
    if (isInitialLoad) {
      prevUsersLengthRef.current = users.length;
      return;
    }

    // 初回の自動取得チェック時は、前回のusers.lengthを記録してスキップ
    // これにより、初回データ取得直後の自動取得を防ぐ
    if (isFirstAutoLoadCheckRef.current) {
      isFirstAutoLoadCheckRef.current = false;
      prevUsersLengthRef.current = users.length;
      return;
    }

    // フィルタリング結果がDISPLAY_INCREMENT未満の場合、自動的に追加取得
    // 無限ループを防ぐため、以下の条件を満たす場合のみ実行：
    // 1. filteredUsers.length < DISPLAY_INCREMENT
    // 2. nextTokenが存在する
    // 3. 前回の自動取得で新しいデータが追加された、または前回取得したnextTokenと異なる（新しいページがある）
    const currentToken = data?.listUsers?.nextToken || null;
    const hasNewToken = currentToken && currentToken !== lastAutoLoadTokenRef.current;
    const hasNewUsers = users.length !== prevUsersLengthRef.current;
    
    // フィルタリング結果が少ない場合でも、nextTokenが存在し、かつ新しいデータが追加されたか、新しいページがある場合は続行
    const shouldAutoLoad = 
      filteredUsers.length < DISPLAY_INCREMENT && 
      currentToken &&
      (hasNewUsers || hasNewToken);

    if (shouldAutoLoad) {
      prevUsersLengthRef.current = users.length;
      lastAutoLoadTokenRef.current = currentToken;
      
      const autoLoadMore = async () => {
        setIsAutoLoading(true);
        try {
          await fetchMore({
            variables: {
              companyId,
              limit: DISPLAY_INCREMENT,
              nextToken: currentToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prev;
              
              // nullチェックを追加し、nullの場合はフィルタリング
              let prevUsers = prev.listUsers.items
                .filter((user: User | null): user is User => user !== null);
              let newUsers = fetchMoreResult.listUsers.items
                .filter((user: User | null): user is User => user !== null);

              // 重複を防ぐために、既存のユーザーIDをチェック
              const existingUserIds = new Set(prevUsers.map((u: User) => u.userId));
              const uniqueNewUsers = newUsers.filter((u: User) => !existingUserIds.has(u.userId));
              const updatedItems = [...prevUsers, ...uniqueNewUsers];
              
              // Apolloキャッシュのみを更新（状態はuseEffectが自動的に更新する）
              return {
                listUsers: {
                  __typename: prev.listUsers.__typename,
                  items: updatedItems,
                  nextToken: fetchMoreResult.listUsers.nextToken
                }
              };
            }
          });
        } catch (error) {
          console.error('ユーザーデータの自動取得に失敗しました:', error);
        } finally {
          setIsAutoLoading(false);
        }
      };

      autoLoadMore();
    } else {
      prevUsersLengthRef.current = users.length;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers.length, users.length, hasMore, listFetchBusy, postFetchPaintHold, clientFilterPaintHold, companyId, currentUser?.managementScope, data?.listUsers?.nextToken]);

  // さらに取得ボタンのクリックハンドラー
  const handleLoadMore = async () => {
    if (data?.listUsers?.nextToken) {
      try {
        await fetchMore({
          variables: {
            companyId,
            limit: DISPLAY_INCREMENT,
            nextToken: data.listUsers.nextToken,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            
            // nullチェックを追加し、nullの場合はフィルタリング
            let prevUsers = prev.listUsers.items
              .filter((user: User | null): user is User => user !== null);
            let newUsers = fetchMoreResult.listUsers.items
              .filter((user: User | null): user is User => user !== null);

            // 重複を防ぐために、既存のユーザーIDをチェック
            const existingUserIds = new Set(prevUsers.map((u: User) => u.userId));
            const uniqueNewUsers = newUsers.filter((u: User) => !existingUserIds.has(u.userId));
            const updatedItems = [...prevUsers, ...uniqueNewUsers];
            
            // Apolloキャッシュのみを更新（状態はuseEffectが自動的に更新する）
            return {
              listUsers: {
                __typename: prev.listUsers.__typename,
                items: updatedItems,
                nextToken: fetchMoreResult.listUsers.nextToken
              }
            };
          }
        });
      } catch (error) {
        console.error('ユーザーデータの取得に失敗しました:', error);
        // エラーが発生した場合でも、ユーザーに通知するか、必要に応じてエラーダイアログを表示
      }
    } else {
      setHasMore(false);
    }
  };

  // ダイアログを閉じる
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  // 作成・編集ダイアログからの保存
  const handleSaveUserFromDialog = async (user: User) => {
    const email = !user.email || user.email.trim() === '' ? null : user.email;
    const departments = user.departments && user.departments.length > 0
      ? user.departments
      : (user.department ? [user.department] : []);
    const isAdmin = user.userRole === 'ADMIN' || user.userRole === 'SYSTEM_ADMIN';
    if (selectedUser) {
      const result = await updateUser({
        variables: {
          input: {
            userId: selectedUser.userId,
            givenUserId: user.givenUserId,
            email,
            displayName: user.displayName,
            departments,
            managementScope: isAdmin ? (user.managementScope || null) : null,
            profileId: isAdmin ? (user.profileId || null) : null,
            userRole: user.userRole,
            disable: user.disable
          }
        },
        refetchQueries: [{ query: LIST_USERS, variables: { companyId, limit: DISPLAY_INCREMENT } }]
      });
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message || 'ユーザー更新中にエラーが発生しました');
      }
    } else {
      if (!user.tempPassword?.trim()) throw new Error('仮パスワードを入力してください');
      const result = await createUser({
        variables: {
          input: {
            givenUserId: user.givenUserId,
            email,
            displayName: user.displayName,
            companyId,
            departments,
            managementScope: isAdmin ? (user.managementScope || null) : null,
            profileId: isAdmin ? (user.profileId || null) : null,
            userRole: user.userRole,
            disable: user.disable,
            tempPassword: user.tempPassword
          }
        },
        refetchQueries: [{ query: LIST_USERS, variables: { companyId, limit: DISPLAY_INCREMENT } }]
      });
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message || 'ユーザー作成中にエラーが発生しました');
      }
    }
    handleCloseDialog();
  };

  // 削除ダイアログを閉じる
  const handleCloseDeleteDialog = () => {
    setDialogErrorMessage(null); // エラーメッセージをクリア
    setOpenDeleteDialog(false);
  };

  // 一括削除ダイアログを開く
  const handleOpenBulkDeleteDialog = () => {
    if (selectedUsers.length === 0) {
      setErrorMessage('ユーザーが選択されていません');
      return;
    }
    setDialogErrorMessage(null);
    setBulkDeleteLoading(false);
    setOpenBulkDeleteDialog(true);
  };

  // 一括削除ダイアログを閉じる
  const handleCloseBulkDeleteDialog = () => {
    if (bulkDeleteLoading) return;
    setDialogErrorMessage(null);
    setBulkDeleteLoading(false);
    setOpenBulkDeleteDialog(false);
  };

  // 一括削除実行ハンドラー
  const handleBulkDelete = async () => {
    const selectableUsers = filteredUsers.filter(u => u.userRole !== 'SYSTEM_ADMIN' && !u.locked);
    const userIdsToDelete = selectedUsers.filter(id => selectableUsers.some(u => u.userId === id));
    if (userIdsToDelete.length === 0) {
      handleCloseBulkDeleteDialog();
      return;
    }

    setBulkDeleteLoading(true);
    setDialogErrorMessage(null);
    setErrorMessage(null);
    try {
      let successCount = 0;
      const errors: string[] = [];

      for (const userId of userIdsToDelete) {
        try {
          const res = await deleteUser({
            variables: { userId },
          });
          if (res.errors && res.errors.length > 0) {
            errors.push(res.errors[0].message || `ユーザー ${userId} の削除に失敗しました`);
          } else {
            successCount++;
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : '削除に失敗しました';
          errors.push(message);
        }
      }

      // 1件以上成功した場合はリストを再取得
      if (successCount > 0) {
        await refetchUsers();
      }

      setSelectedUsers([]);
      setOpenBulkDeleteDialog(false);
      setDialogErrorMessage(null);

      if (errors.length > 0) {
        setErrorMessage(
          `${successCount}件削除しました。${errors.length}件は削除に失敗しました: ${errors.join('; ')}`
        );
      } else {
        setInfoMessage(`${successCount}人のユーザーを削除しました`);
      }
    } catch (error: unknown) {
      console.error('一括削除に失敗しました:', error);
      setDialogErrorMessage(
        error instanceof Error ? error.message : '一括削除中にエラーが発生しました'
      );
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // パスワード変更ダイアログを閉じる
  const handleClosePasswordDialog = () => {
    setDialogErrorMessage(null); // エラーメッセージをクリア
    setOpenPasswordDialog(false);
  };

  // パスワードフォーム入力変更ハンドラー
  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  // パスワード変更チェックボックス変更ハンドラー
  const handleForceChangeToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({
      ...passwordData,
      forceChange: e.target.checked
    });
  };

  // パスワード変更ボタンの無効化判定
  const isPasswordChangeDisabled = () => {
    // 新しいパスワードが空の場合
    if (!passwordData.newPassword.trim()) {
      return true;
    }
    
    // 確認パスワードが空の場合
    if (!passwordData.confirmPassword.trim()) {
      return true;
    }
    
    // パスワードが一致しない場合
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return true;
    }
    
    // パスワードポリシーの検証
    const validationResult = validatePassword(passwordData.newPassword);
    if (!validationResult.isValid) {
      return true;
    }
    
    return false;
  };

  // パスワード変更ハンドラー
  const handleResetPassword = async () => {
    if (!selectedUser) return;

    // パスワードの検証
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setDialogErrorMessage('パスワードが一致しません');
      return;
    }

    // パスワードポリシーの検証
    const validationResult = validatePassword(passwordData.newPassword);
    if (!validationResult.isValid) {
      setDialogErrorMessage(validationResult.errorMessage || 'パスワードが要件を満たしていません');
      return;
    }

    try {
      // authService.tsで実装したadminResetUserPasswordを使用
      const { authService } = await import('../../services/authService');
      const result = await authService.adminResetUserPassword(
        selectedUser.givenUserId,
        passwordData.newPassword,
        !passwordData.forceChange // permanent: trueの場合、次回ログイン時にパスワード変更が不要
      );

      if (result.success) {
        handleClosePasswordDialog();
        // 成功メッセージを表示
        alert('パスワードが正常にリセットされました');
      } else {
        setDialogErrorMessage(`パスワードリセットに失敗しました: ${result.message}`);
      }
    } catch (error: any) {
      console.error('パスワードリセットエラー:', error);
      setDialogErrorMessage(error.message || 'パスワードリセット中にエラーが発生しました');
    }
  };

  // ユーザー削除ハンドラー
  const handleDeleteUser = async () => {
    if (selectedUser) {
      try {
        const res = await deleteUser({
          variables: { userId: selectedUser.userId },
          refetchQueries: [
            {
              query: LIST_USERS,
              variables: { companyId, limit: DISPLAY_INCREMENT },
            },
          ],
        });

        // GraphQLのレスポンスにエラーが含まれているかチェック
        if (res.errors && res.errors.length > 0) {
          // エラーメッセージをダイアログ内に表示
          const errorMessage = res.errors[0].message || 'ユーザーの削除に失敗しました';
          setDialogErrorMessage(errorMessage);
          return; // エラーがある場合はダイアログを閉じない
        }
        
        // 成功した場合のみダイアログを閉じる
        handleCloseDeleteDialog();
      } catch (error: any) {
        console.error('ユーザーの削除に失敗しました:', error);
        // GraphQLのエラーメッセージをダイアログ内に表示
        setDialogErrorMessage(error.message || 'ユーザーの削除に失敗しました');
      }
    }
  };

  // CSVダウンロードハンドラー（全ページを取得してからダウンロード）
  const handleCsvDownload = async () => {
    setCsvDownloadLoading(true);
    try {
      // 全ページのデータを取得
      let allUsers: User[] = [...users];
      let currentToken = data?.listUsers?.nextToken;

      // nextTokenがある限り、全ページを取得
      while (currentToken) {
        try {
          const result = await fetchMore({
            variables: {
              companyId,
              limit: DISPLAY_INCREMENT,
              nextToken: currentToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prev;
              
              // nullチェックを追加し、nullの場合はフィルタリング
              let prevUsers = prev.listUsers.items
                .filter((user: User | null): user is User => user !== null);
              let newUsers = fetchMoreResult.listUsers.items
                .filter((user: User | null): user is User => user !== null);

              // currentUser?.managementScopeに基づくフィルタリング
              if (currentUser?.managementScope && currentUser.managementScope.trim() !== '') {
                const scope = currentUser.managementScope.trim();
                const inScope = (user: User) => {
                  const depts = user.departments && user.departments.length > 0
                    ? user.departments
                    : (user.department ? [user.department] : []);
                  return depts.includes(scope);
                };
                prevUsers = prevUsers.filter(inScope);
                newUsers = newUsers.filter(inScope);
              }

              const updatedItems = [...prevUsers, ...newUsers];
              
              return {
                listUsers: {
                  __typename: prev.listUsers.__typename,
                  items: updatedItems,
                  nextToken: fetchMoreResult.listUsers.nextToken
                }
              };
            }
          });

          // result.dataから新しいユーザーを取得してallUsersに追加
          if (result.data?.listUsers?.items) {
            let newUsers = result.data.listUsers.items
              .filter((user: User | null): user is User => user !== null);

            // currentUser?.managementScopeに基づくフィルタリング
            if (currentUser?.managementScope && currentUser.managementScope.trim() !== '') {
              const scope = currentUser.managementScope.trim();
              newUsers = newUsers.filter((user: User) => {
                const depts = user.departments && user.departments.length > 0
                  ? user.departments
                  : (user.department ? [user.department] : []);
                return depts.includes(scope);
              });
            }

            // 既にallUsersに含まれているユーザーを除外（重複防止）
            const existingUserIds = new Set(allUsers.map((u: User) => u.userId));
            const uniqueNewUsers = newUsers.filter((u: User) => !existingUserIds.has(u.userId));
            allUsers = [...allUsers, ...uniqueNewUsers];
          }

          currentToken = result.data?.listUsers?.nextToken || null;
        } catch (error) {
          console.error('ユーザーデータの取得に失敗しました:', error);
          // エラーが発生した場合は、取得できた分までで処理を続行
          break;
        }
      }

      // 検索キーワードによるフィルタリングを適用
      let usersToExport = allUsers;
      if (searchKeywords.length > 0) {
        usersToExport = allUsers.filter((user: User) => {
          return searchKeywords.some(keyword => {
            const term = keyword.trim().toLowerCase();
            if (!term) return false;
            
            return (
              (user.displayName && user.displayName.toLowerCase().includes(term)) ||
              (user.givenUserId && user.givenUserId.toLowerCase().includes(term)) ||
              (user.departments && user.departments.length > 0
                ? user.departments.some(d => d.toLowerCase().includes(term))
                : (user.department ? user.department.toLowerCase().includes(term) : false))
            );
          });
        });
      }

      // CSVエスケープ（RFC4180 準拠）
      const escapeCsv = escapeCsvField;
      const escapeMulti = (values: string[] | undefined): string => {
        const joined = (values || []).map((v) => String(v).trim()).filter(Boolean).join(',');
        if (!joined) return '';
        // 複数所属は常にダブルクォートで囲む仕様
        return `"${joined.replace(/"/g, '""')}"`;
      };
      const profileNameById = new Map<string, string>(profiles.map((p) => [p.profileId, p.name]));

      // CSVヘッダー
      const headers = [
        'ユーザーID',
        'メールアドレス',
        '氏名',
        '所属（複数可、カンマ区切り）',
        '管理範囲',
        'プロファイル名',
        '権限（ユーザー|管理者）',
        'ステータス（有効|無効）',
        '仮パスワード',
        '最終ログイン',
      ];

      // CSVデータ行を生成
      const csvRows = [
        headers.join(','),
        ...usersToExport.map(user => {
          const depts = user.departments && user.departments.length > 0
            ? user.departments
            : (user.department ? [user.department] : []);
          const profileName = user.profileId ? (profileNameById.get(user.profileId) || '') : '';
          return [
            escapeCsv(user.givenUserId),
            escapeCsv(user.email),
            escapeCsv(user.displayName),
            escapeMulti(depts),
            escapeCsv(user.managementScope || ''),
            escapeCsv(profileName),
            escapeCsv(user.userRole === 'ADMIN' ? '管理者' : user.userRole === 'SYSTEM_ADMIN' ? 'システム管理者' : 'ユーザー'),
            escapeCsv(user.disable ? '無効' : '有効'),
            escapeCsv(''),
            escapeCsv(user.lastLoginDate ? formatDate(user.lastLoginDate) : '')
          ].join(',');
        })
      ];

      // BOMを追加してUTF-8でエンコード（Excelで正しく開けるように）
      const csvContent = '\uFEFF' + csvRows.join('\n');
      
      // Blobオブジェクトを作成
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // ダウンロードリンクを作成
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // ファイル名を設定
      const fileName = `users_${new Date().toISOString().split('T')[0]}.csv`;
      link.download = fileName;
      
      // リンクをクリックしてダウンロード
      document.body.appendChild(link);
      link.click();
      
      // クリーンアップ
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSVダウンロード中にエラーが発生しました:', error);
      alert('CSVダウンロード中にエラーが発生しました。');
    } finally {
      setCsvDownloadLoading(false);
    }
  };

  // CSVインポート関連の状態
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [importPreview, setImportPreview] = useState<string[][]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [csvDownloadLoading, setCsvDownloadLoading] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [importResult, setImportResult] = useState<{
    successCount: { created: number; updated: number };
    skippedCount: number;
    capacityCheckFailed: boolean;
    errors: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // CSVインポートダイアログを開く
  const handleOpenImportDialog = () => {
    setCsvData([]);
    setCsvErrors([]);
    setImportPreview([]);
    setOpenImportDialog(true);
  };

  // CSVインポートダイアログを閉じる
  const handleCloseImportDialog = () => {
    setOpenImportDialog(false);
    // ファイル入力をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 確認ダイアログを閉じる
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  // エラーダイアログを開く
  const handleOpenErrorDialog = (result: {
    successCount: { created: number; updated: number };
    skippedCount: number;
    capacityCheckFailed: boolean;
    errors: string[];
  }) => {
    setImportResult(result);
    setOpenErrorDialog(true);
  };

  // エラーダイアログを閉じる
  const handleCloseErrorDialog = () => {
    setOpenErrorDialog(false);
    setImportResult(null);
  };

  // CSVファイル選択ハンドラー
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック（5MBまで）
    if (file.size > 5 * 1024 * 1024) {
      setCsvErrors(['ファイルサイズが大きすぎます。5MB以下のファイルを選択してください。']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        // BOM を取り除いた上で RFC4180 準拠でパース
        const cleaned = content.replace(/^\uFEFF/, '');
        const parsedData = parseCsv(cleaned);

        // 最大行数チェック（ヘッダー + データ行）
        if (parsedData.length > CSV_IMPORT_MAX_ROWS + 1) {
          setCsvErrors([`CSVファイルの行数が多すぎます。最大${CSV_IMPORT_MAX_ROWS.toLocaleString()}行までインポート可能です。`]);
          return;
        }

        const headersRow = parsedData[0] || [];
        const header0 = (headersRow[0] || '').trim();
        if (header0 !== 'ユーザーID') {
          setCsvErrors(['CSVヘッダーが正しくありません。先頭列は「ユーザーID」である必要があります。']);
          return;
        }
        if (!(headersRow[3] || '').includes('所属')) {
          setCsvErrors(['CSVフォーマットが旧形式です。CSVダウンロードで最新フォーマットを取得してから再度インポートしてください。']);
          return;
        }

        const columnIndex = { userId: 0, email: 1, displayName: 2, departments: 3, scope: 4, profile: 5, role: 6, status: 7, password: 8 };

        // データ行のバリデーション
        const errors: string[] = [];
        const userIds = new Set<string>();
        const dataRows = parsedData.slice(1);
        const profileNames = new Set(profiles.map((p) => p.name));

        dataRows.forEach((row, index) => {
          const lineNumber = index + 2;
          if (row.length < 9) {
            errors.push(`${lineNumber}行目: 列数が不足しています`);
            return;
          }

          const userId = (row[columnIndex.userId] || '').trim();
          const email = (row[columnIndex.email] || '').trim();
          const displayName = (row[columnIndex.displayName] || '').trim();
          const departments = parseMultiValue(row[columnIndex.departments]);
          const managementScope = columnIndex.scope >= 0 ? (row[columnIndex.scope] || '').trim() : '';
          const profileName = columnIndex.profile >= 0 ? (row[columnIndex.profile] || '').trim() : '';
          const role = (row[columnIndex.role] || '').trim();
          const status = (row[columnIndex.status] || '').trim();
          const password = (row[columnIndex.password] || '').trim();

          const userToValidate = {
            givenUserId: userId,
            email,
            displayName,
            department: departments[0] || '',
            userRole: role === '管理者' ? 'ADMIN' : role === 'システム管理者' ? 'SYSTEM_ADMIN' : 'USER',
            disable: status === '無効',
            tempPassword: password
          };

          const validatePasswordField = !!password;
          const validationResult = validateUser(userToValidate, validatePasswordField);
          if (!validationResult.isValid) {
            validationResult.errors.forEach(error => {
              errors.push(`${lineNumber}行目: ${error.message}`);
            });
          }

          if (userId) {
            if (userIds.has(userId)) {
              errors.push(`${lineNumber}行目: ユーザーID「${userId}」が重複しています`);
            } else {
              userIds.add(userId);
            }
          }

          if (status && status !== '有効' && status !== '無効') {
            errors.push(`${lineNumber}行目: ステータスは「有効」または「無効」である必要があります`);
          }

          // ADMIN はプロファイル必須
          if (userToValidate.userRole === 'ADMIN' && !profileName) {
            errors.push(`${lineNumber}行目: 管理者にはプロファイルの指定が必要です`);
          }

          // プロファイル名の存在チェック（指定時のみ、管理者行のみ）
          if (profileName) {
            if (userToValidate.userRole !== 'ADMIN' && userToValidate.userRole !== 'SYSTEM_ADMIN') {
              errors.push(`${lineNumber}行目: プロファイルは管理者にのみ設定できます`);
            } else if (!profileNames.has(profileName)) {
              errors.push(`${lineNumber}行目: プロファイル「${profileName}」は存在しません`);
            }
          }

          if (managementScope && userToValidate.userRole === 'USER') {
            errors.push(`${lineNumber}行目: 管理範囲は管理者にのみ設定できます`);
          }

          // 管理者かつ管理範囲が設定されている場合、行内の所属リストに含まれている必要がある
          if (managementScope && (userToValidate.userRole === 'ADMIN' || userToValidate.userRole === 'SYSTEM_ADMIN')) {
            if (!departments.includes(managementScope)) {
              errors.push(`${lineNumber}行目: 管理範囲（${managementScope}）が所属に含まれていません`);
            }
          }

          // 管理者は所属が一つでも設定されている場合、管理範囲のブランクは不可（所属のいずれかを管理範囲に指定する必要がある）
          if (
            (userToValidate.userRole === 'ADMIN' || userToValidate.userRole === 'SYSTEM_ADMIN') &&
            departments.length > 0 &&
            !managementScope
          ) {
            errors.push(`${lineNumber}行目: 所属が設定されている管理者は管理範囲を空欄にできません（所属のいずれかを管理範囲に指定してください）`);
          }

          // 自身の管理範囲が設定されている場合、ADMIN の管理範囲は自身の管理範囲と一致しなければならない
          if (
            currentUser?.managementScope &&
            currentUser.managementScope.trim() !== '' &&
            userToValidate.userRole === 'ADMIN'
          ) {
            if (managementScope !== currentUser.managementScope.trim()) {
              errors.push(`${lineNumber}行目: 管理者の管理範囲は「${currentUser.managementScope}」と一致する必要があります`);
            }
          }

          // 管理範囲フィルタ：managementScope がある場合のみ制限。ない場合は制限なし（バックエンドの withinManagementScope と同一ロジック）
          if (currentUser?.managementScope && currentUser.managementScope.trim() !== '') {
            if (!departments.includes(currentUser.managementScope)) {
              errors.push(`${lineNumber}行目: 管理範囲外（「${currentUser.managementScope}」を所属に含む必要があります）`);
            }
          }
        });

        if (errors.length > 0) {
          setCsvErrors(errors);
          return;
        }

        // プレビュー用に先頭5行を保存
        const preview = [headersRow, ...dataRows.slice(0, 5)];
        setImportPreview(preview);
        setCsvData(parsedData);
        setOpenConfirmDialog(true);
      } catch (error) {
        console.error('CSVパースエラー:', error);
        setCsvErrors(['CSVファイルの解析中にエラーが発生しました。ファイル形式を確認してください。']);
      }
    };

    reader.onerror = () => {
      setCsvErrors(['ファイルの読み込み中にエラーが発生しました。']);
    };

    reader.readAsText(file);
  };

  // CSVインポート実行（bulkImportUsers 一括API）
  const handleExecuteImport = async () => {
    if (csvData.length <= 1 || !companyId) return;

    setImportLoading(true);
    const dataRows = csvData.slice(1);

    const formatBulkRowMessage = (
      rowIndex: number,
      givenUserId: string | null | undefined,
      message: string
    ) => {
      if (rowIndex <= 0) {
        return message;
      }
      const csvLine = rowIndex + 1;
      const idPart = givenUserId ? `（ユーザーID: ${givenUserId}）` : '';
      return `${csvLine}行目${idPart}: ${message}`;
    };

    try {
      const colIdx = { userId: 0, email: 1, displayName: 2, departments: 3, scope: 4, profile: 5, role: 6, status: 7, password: 8 };

      // プロファイル名 → profileId の解決マップ
      const profileByName = new Map(profiles.map((p) => [p.name, p]));

      const users = dataRows.map((row) => {
        const userId = (row[colIdx.userId] || '').trim();
        const email = (row[colIdx.email] || '').trim();
        const displayName = (row[colIdx.displayName] || '').trim();
        const departments = parseMultiValue(row[colIdx.departments]);
        const roleCell = (row[colIdx.role] || '').trim();
        const role = roleCell === '管理者' ? 'ADMIN' : roleCell === 'システム管理者' ? 'SYSTEM_ADMIN' : 'USER';
        const disable = (row[colIdx.status] || '').trim() === '無効';
        const password = (row[colIdx.password] || '').trim();
        const scopeCell = (row[colIdx.scope] || '').trim();
        const profileNameCell = (row[colIdx.profile] || '').trim();
        const resolvedProfileId = profileNameCell ? (profileByName.get(profileNameCell)?.profileId ?? null) : null;
        const rowInput: {
          givenUserId: string;
          email?: string | null;
          displayName: string;
          department?: string | null;
          departments?: string[];
          managementScope?: string | null;
          profileId?: string | null;
          userRole: 'ADMIN' | 'USER' | 'SYSTEM_ADMIN';
          disable: boolean;
          tempPassword?: string | null;
        } = {
          givenUserId: userId,
          displayName,
          department: departments[0] || null,
          departments: departments.length > 0 ? departments : undefined,
          userRole: role,
          disable,
          managementScope: scopeCell || null,
          profileId: resolvedProfileId,
        };
        rowInput.email = email === '' ? null : email;
        if (password !== '') {
          rowInput.tempPassword = password;
        }
        return rowInput;
      });

      let totalCreated = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      let capacityCheckFailed = false;
      const lineErrors: string[] = [];

      for (let chunkIndex = 0; chunkIndex < users.length; chunkIndex += CSV_IMPORT_CHUNK_SIZE) {
        const chunk = users.slice(chunkIndex, chunkIndex + CSV_IMPORT_CHUNK_SIZE);
        const rowOffset = chunkIndex;

        const { data: mutData, errors: graphQLErrors } = await bulkImportUsers({
          variables: {
            input: {
              companyId,
              users: chunk,
            },
          },
        });

        if (graphQLErrors?.length) {
          setCsvErrors(graphQLErrors.map((e) => e.message));
          return;
        }

        const res = mutData?.bulkImportUsers;
        if (!res) {
          setCsvErrors(['サーバーから結果を取得できませんでした。']);
          return;
        }

        if (res.validationFailed) {
          for (const e of res.validationErrors) {
            lineErrors.push(formatBulkRowMessage(e.rowIndex + rowOffset, e.givenUserId, e.message));
          }
        }
        for (const e of res.processingErrors) {
          lineErrors.push(formatBulkRowMessage(e.rowIndex + rowOffset, e.givenUserId, e.message));
        }

        totalCreated += res.createdCount;
        totalUpdated += res.updatedCount;
        totalSkipped += res.skippedCount;
        capacityCheckFailed = capacityCheckFailed || res.capacityCheckFailed;
      }

      await refetchUsers();

      const successCount = { created: totalCreated, updated: totalUpdated };
      const skippedCount = totalSkipped;
      const hasIssue = lineErrors.length > 0 || capacityCheckFailed;

      if (hasIssue) {
        handleCloseConfirmDialog();
        handleCloseImportDialog();
        handleOpenErrorDialog({
          successCount,
          skippedCount,
          capacityCheckFailed,
          errors: lineErrors,
        });
      } else {
        const skipPart = skippedCount > 0 ? `、スキップ ${skippedCount}件` : '';
        alert(`インポート完了: ${successCount.created}件作成、${successCount.updated}件更新${skipPart}`);
        handleCloseConfirmDialog();
        handleCloseImportDialog();
      }
    } catch (error: unknown) {
      console.error('インポート処理エラー:', error);
      const msg = error instanceof Error ? error.message : 'インポート処理中にエラーが発生しました';
      setCsvErrors([msg]);
    } finally {
      setImportLoading(false);
    }
  };

  // サンプルCSVダウンロードハンドラー
  const handleSampleCsvDownload = () => {
    const headers = [
      'ユーザーID',
      'メールアドレス',
      '氏名',
      '所属（複数可、カンマ区切り）',
      '管理範囲',
      'プロファイル名',
      '権限（ユーザー|管理者）',
      'ステータス（有効|無効）',
      '仮パスワード',
      '最終ログイン',
    ];
    const sampleRow = [
      escapeCsvField('sample_user'),
      escapeCsvField('sample@example.com'),
      escapeCsvField('サンプル 太郎'),
      escapeCsvField('営業部'),
      escapeCsvField(''),
      escapeCsvField(''),
      escapeCsvField('ユーザー'),
      escapeCsvField('有効'),
      escapeCsvField('Passw0rd!'),
      escapeCsvField(''),
    ];
    const csvContent = '﻿' + [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users_sample.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // CSVインポートハンドラー
  const handleCsvImport = () => {
    handleOpenImportDialog();
  };

  // ステータス変更実行ハンドラー（再フェッチは全件の mutation 完了後に1回だけ）
  const handleExecuteStatusChange = async () => {
    if (selectedUsers.length === 0) {
      setErrorMessage('ユーザーが選択されていません');
      return;
    }

    const overlayText =
      batchActionValue === '0'
        ? '選択したユーザーを有効化しています…'
        : '選択したユーザーを無効化しています…';
    flushSync(() => {
      setBatchStatusOverlayMessage(overlayText);
      setBatchStatusChangeLoading(true);
    });

    try {
      for (const userId of selectedUsers) {
        const user = users.find(u => u.userId === userId);
        if (user) {
          if (batchActionValue === '0') {
            await enableUser({ variables: { userId } });
          } else {
            await disableUser({ variables: { userId } });
          }
        }
      }

      await refetchUsers();
      const selectedCount = selectedUsers.length;
      setSelectedUsers([]);
      setInfoMessage(`${selectedCount}人のユーザーのステータスを変更しました`);
    } catch (error: unknown) {
      console.error('ステータス変更に失敗しました:', error);
      setErrorMessage(
        error instanceof Error ? error.message : 'ステータス変更中にエラーが発生しました'
      );
      await refetchUsers();
    } finally {
      flushSync(() => {
        setBatchStatusChangeLoading(false);
        setBatchStatusOverlayMessage('');
      });
    }
  };

  // チェックボックス変更ハンドラー
  const handleCheckboxChange = useCallback((userId: string) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  }, []);

  // 全選択/全解除
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedUsers(filteredUsers.filter(u => u.userRole !== 'SYSTEM_ADMIN' && !u.locked).map(u => u.userId));
    } else {
      setSelectedUsers([]);
    }
  }, [filteredUsers]);

  const formatDateStable = useCallback((dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }, []);

  const handleOpenDialogStable = useCallback((user?: User) => {
    setSelectedUser(user ?? null);
    setOpenDialog(true);
  }, []);

  const handleOpenDeleteDialogStable = useCallback((user: User) => {
    setSelectedUser(user);
    setDialogErrorMessage(null);
    setOpenDeleteDialog(true);
  }, []);

  const handleOpenPasswordDialogStable = useCallback((user: User) => {
    setSelectedUser(user);
    setDialogErrorMessage(null);
    setPasswordData({ newPassword: '', confirmPassword: '', forceChange: true });
    setOpenPasswordDialog(true);
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      {showUserListOverlay && (
        <Box
          role="status"
          aria-live="polite"
          aria-busy="true"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: (t) => t.zIndex.modal - 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: 'rgba(255, 255, 255, 0.88)',
            backdropFilter: 'blur(2px)',
            pointerEvents: 'all',
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            {listFetchBusy
              ? 'ユーザー一覧を取得しています…'
              : clientFilterPaintHold
                ? '検索・絞り込み結果を一覧に反映しています…'
                : '一覧を表示しています…'}
          </Typography>
        </Box>
      )}
      {batchStatusChangeLoading && typeof document !== 'undefined'
        ? createPortal(
            <Box
              role="status"
              aria-live="assertive"
              aria-busy="true"
              aria-label={batchStatusOverlayMessage || 'ステータスを更新しています'}
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: theme.zIndex.modal + 500,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                bgcolor: 'rgba(255, 255, 255, 0.94)',
                backdropFilter: 'blur(3px)',
                pointerEvents: 'all',
              }}
            >
              <CircularProgress size={48} thickness={4} />
              <Typography variant="body1" color="text.primary" sx={{ px: 2, textAlign: 'center', maxWidth: 360 }}>
                {batchStatusOverlayMessage || 'ステータスを更新しています…'}
              </Typography>
            </Box>,
            document.body
          )
        : null}
      {/* エラーメッセージ表示 */}
      {errorMessage && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}
      {/* 情報メッセージ表示（有効/無効・削除の成功通知） */}
      {infoMessage && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          onClose={() => setInfoMessage(null)}
        >
          {infoMessage}
        </Alert>
      )}
      {/* ユーザーデータ操作ボタン */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 2, 
        mb: 2,
        mt: 1
      }}>
        <Button
          variant="contained"
          startIcon={csvDownloadLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          onClick={handleCsvDownload}
          disabled={csvDownloadLoading || !permCsvDownload}
          sx={{ 
            bgcolor: theme.palette.smartSkillTalk.linkBlue,
            color: 'white',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              opacity: 0.9
            }
          }}
        >
          {csvDownloadLoading ? 'ダウンロード中...' : 'CSVダウンロード'}
        </Button>
        <Button
          variant="contained"
          startIcon={<FileUploadIcon />}
          onClick={handleCsvImport}
          disabled={!permCsvImport}
          sx={{
            bgcolor: theme.palette.smartSkillTalk.linkBlue,
            color: 'white',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              opacity: 0.9
            }
          }}
        >
          CSVインポート
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialogStable()}
          disabled={!permCreateNew}
          sx={{ 
            bgcolor: '#5EB600',
            color: 'white',
            borderRadius: '10px',
            '&:hover': {
              bgcolor: '#5EB600',
              opacity: 0.9
            }
          }}
        >
          新規登録
        </Button>
      </Box>

      {/* 検索エリア */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        my: 2,
        py: 2
      }}>

        {/* 実行エリア：有効/無効/削除を1つのドロップダウンで選択してから実行 */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={batchActionValue}
              onChange={(e) => setBatchActionValue(e.target.value as '0' | '1' | 'delete')}
              displayEmpty
              disabled={batchStatusChangeLoading || bulkDeleteLoading || !permBulkUpdate}
              sx={{
                bgcolor: '#F9F9F9',
                borderRadius: '4px',
                borderColor: '#898E93',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#898E93',
                }
              }}
            >
              <MenuItem value="0">有効</MenuItem>
              <MenuItem value="1">無効</MenuItem>
              <MenuItem value="delete">削除</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="contained"
            startIcon={
              batchStatusChangeLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : batchActionValue === 'delete' ? (
                <DeleteIcon />
              ) : (
                <CheckIcon />
              )
            }
            onClick={() => {
              if (batchActionValue === 'delete') {
                handleOpenBulkDeleteDialog();
              } else {
                handleExecuteStatusChange();
              }
            }}
            disabled={selectedUsers.length === 0 || batchStatusChangeLoading || bulkDeleteLoading || !permBulkUpdate}
            sx={{ 
              bgcolor: batchActionValue === 'delete' ? '#FF4D4D' : '#A2A2A2',
              color: 'white',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: batchActionValue === 'delete' ? '#FF4D4D' : '#A2A2A2',
                opacity: 0.9
              }
            }}
          >
            実行{selectedUsers.length > 0 && ` (${selectedUsers.length}件)`}
          </Button>
        </Box>

        {/* 検索フィールドとボタン */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            multiple
            freeSolo
            clearText="検索クリア"
            clearIcon={<DeleteForeverIcon fontSize="small" />}
            options={[]}
            value={searchKeywords}
            inputValue={inputValue}
            onChange={(event, newValue, reason) => {
              // 右端の Clear のみ「ページ初期化」：メモリ上の大量リストを一度捨てて先頭ページから取り直す
              if (reason === 'clear') {
                filterEffectGenRef.current += 1;
                setClientFilterPaintHold(false);
                setListHydrationPaused(true);
                setSearchKeywords([]);
                setInputValue('');
                setUsers([]);
                setFilteredUsers([]);
                setSelectedUsers([]);
                setHasMore(true);
                lastAutoLoadTokenRef.current = null;
                prevUsersLengthRef.current = 0;
                isFirstAutoLoadCheckRef.current = true;
                if (companyId) {
                  void refetchUsers({ companyId, limit: DISPLAY_INCREMENT }).finally(() => {
                    setListHydrationPaused(false);
                  });
                } else {
                  setListHydrationPaused(false);
                }
                return;
              }
              setSearchKeywords(newValue);
            }}
            onInputChange={(event, newInputValue, reason) => {
              // 入力値を更新（resetの場合は空文字になる）
              setInputValue(newInputValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option}
                    {...tagProps}
                    sx={{
                      bgcolor: '#E0E0E0',
                      color: '#424242',
                      '& .MuiChip-deleteIcon': {
                        color: '#616161',
                        '&:hover': {
                          color: '#212121'
                        }
                      }
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={searchKeywords.length === 0 ? "検索項目：氏名、ログインID、所属 OR検索" : ""}
                variant="outlined"
                size="small"
                onKeyDown={(e) => {
                  // スペースキーまたはEnterキーで入力を確定
                  if ((e.key === ' ' || e.key === 'Enter') && inputValue.trim()) {
                    e.preventDefault();
                    const trimmedValue = inputValue.trim();
                    if (trimmedValue && !searchKeywords.includes(trimmedValue)) {
                      setSearchKeywords([...searchKeywords, trimmedValue]);
                      setInputValue('');
                    }
                  }
                }}
                inputProps={{ ...params.inputProps, maxLength: 600 }}
                sx={{
                  width: '600px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '3px',
                    bgcolor: '#F9F9F9',
                    '& fieldset': {
                      borderColor: '#898E93',
                    }
                  }
                }}
              />
            )}
          />
        </Box>

      </Box>

      {/* テーブル */}
      <UserTable
        users={filteredUsers}
        loading={listFetchBusy && !batchStatusChangeLoading}
        error={error}
        selectedUsers={selectedUsers}
        formatDate={formatDateStable}
        onEdit={handleOpenDialogStable}
        onDelete={handleOpenDeleteDialogStable}
        onPassword={handleOpenPasswordDialogStable}
        onSelectUser={handleCheckboxChange}
        onSelectAll={handleSelectAll}
        permBulkUpdate={permBulkUpdate}
        permUpdate={permUpdate}
        permDelete={permDelete}
        permChangePassword={permChangePassword}
      />

      {/* さらに表示するボタン */}
      {/* フィルタリング結果が0件で自動取得が進行中でない場合は表示しない */}
      {hasMore && (filteredUsers.length > 0 || isAutoLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button 
            variant="contained" 
            onClick={handleLoadMore}
            disabled={listFetchBusy}
            sx={{ 
              bgcolor: '#6C9AD7',
              color: 'white',
              fontSize: '16px',
              '&:hover': {
                bgcolor: '#6C9AD7',
                opacity: 0.9
              }
            }}
          >
            {listFetchBusy ? '読み込み中...' : 'さらに表示する'}
          </Button>
        </Box>
      )}

      {/* CSVインポートダイアログ */}
      <Dialog
        open={openImportDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseImportDialog();
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: theme.palette.smartSkillTalk.linkBlue, 
          color: 'white',
          fontSize: '18px',
          fontWeight: 400
        }}>
          CSVインポート
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            CSVファイルからユーザーデータをインポートします。<br />
            CSVフォーマットはダウンロード時と同じ形式を使用してください。
            （<Link
              component="button"
              variant="body2"
              onClick={handleSampleCsvDownload}
              sx={{ verticalAlign: 'baseline', cursor: 'pointer' }}
            >
              サンプルCSV
            </Link>）<br />
            最大{CSV_IMPORT_MAX_ROWS.toLocaleString()}行までインポート可能です。
          </DialogContentText>
          
          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="csv-file-input"
            />
            <label htmlFor="csv-file-input">
              <Button
                variant="contained"
                component="span"
                startIcon={<FileUploadIcon />}
                sx={{ 
                  bgcolor: theme.palette.smartSkillTalk.linkBlue,
                  color: 'white',
                  borderRadius: '10px',
                  '&:hover': {
                    bgcolor: theme.palette.smartSkillTalk.linkBlue,
                    opacity: 0.9
                  }
                }}
              >
                CSVファイルを選択
              </Button>
            </label>
          </Box>
          
          {csvErrors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" sx={{ mb: 1 }}>
                CSVファイルにエラーがあります
              </Alert>
              <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 2, bgcolor: '#FFF8F8' }}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {csvErrors.map((error, index) => (
                    <li key={index} style={{ color: '#D32F2F', marginBottom: 4 }}>
                      {error}
                    </li>
                  ))}
                </ul>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseImportDialog}
            sx={{ 
              color: theme.palette.smartSkillTalk.linkBlue,
              borderColor: theme.palette.smartSkillTalk.linkBlue,
              borderRadius: '6px',
              '&:hover': {
                borderColor: theme.palette.smartSkillTalk.linkBlue,
                opacity: 0.9
              }
            }}
            variant="outlined"
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* インポート確認ダイアログ */}
      <Dialog
        open={openConfirmDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseConfirmDialog();
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: theme.palette.smartSkillTalk.linkBlue, 
          color: 'white',
          fontSize: '18px',
          fontWeight: 400
        }}>
          インポート確認
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            以下のデータをインポートします。よろしいですか？<br />
            （全{csvData.length - 1}件、先頭5件のプレビュー）
          </DialogContentText>
          
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#F0F7FF' }}>
                <TableRow>
                  {importPreview[0]?.map((header, index) => (
                    <TableCell key={index} align="center" sx={{ 
                      color: theme.palette.smartSkillTalk.linkBlue,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap'
                    }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {importPreview.slice(1).map((row, rowIndex) => (
                  <TableRow key={rowIndex} sx={{ 
                    bgcolor: rowIndex % 2 === 0 ? '#FFFFFF' : '#F9F9F9',
                  }}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex} align="center" sx={{ 
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            注意: 既存のユーザーIDがある場合は更新、ない場合は新規作成されます。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleExecuteImport}
            variant="contained"
            disabled={importLoading}
            startIcon={importLoading ? <CircularProgress size={20} /> : null}
            sx={{ 
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              color: 'white',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: theme.palette.smartSkillTalk.linkBlue,
                opacity: 0.9
              }
            }}
          >
            {importLoading ? 'インポート中...' : 'インポート実行'}
          </Button>
          <Button 
            onClick={handleCloseConfirmDialog}
            disabled={importLoading}
            sx={{ 
              color: theme.palette.smartSkillTalk.linkBlue,
              borderColor: theme.palette.smartSkillTalk.linkBlue,
              borderRadius: '6px',
              '&:hover': {
                borderColor: theme.palette.smartSkillTalk.linkBlue,
                opacity: 0.9
              }
            }}
            variant="outlined"
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* インポート結果ダイアログ */}
      <Dialog
        open={openErrorDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseErrorDialog();
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor:
            importResult?.errors.length || importResult?.capacityCheckFailed
              ? '#FF4D4D'
              : theme.palette.smartSkillTalk.linkBlue,
          color: 'white',
          fontSize: '18px',
          fontWeight: 400
        }}>
          インポート結果
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {importResult && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>
                  処理結果
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  作成: {importResult.successCount.created}件
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  更新: {importResult.successCount.updated}件
                </Typography>
                {importResult.skippedCount > 0 && (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    スキップ: {importResult.skippedCount}件
                  </Typography>
                )}
              </Box>

              {importResult.capacityCheckFailed && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  登録人数の上限により、新規作成がスキップされた行があります。詳細は下記を確認してください。
                </Alert>
              )}
              
              {importResult.errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {importResult.errors.length}件のメッセージがあります
                  </Alert>
                  <Typography variant="h6" sx={{ mb: 1, color: '#D32F2F' }}>
                    エラー詳細
                  </Typography>
                  <Paper sx={{ 
                    maxHeight: 300, 
                    overflow: 'auto', 
                    p: 2, 
                    bgcolor: '#FFF8F8',
                    border: '1px solid #FFCDD2'
                  }}>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {importResult.errors.map((error, index) => (
                        <li key={index} style={{ 
                          color: '#D32F2F', 
                          marginBottom: 8,
                          lineHeight: 1.5
                        }}>
                          {error}
                        </li>
                      ))}
                    </ul>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseErrorDialog}
            variant="contained"
            sx={{ 
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              color: 'white',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: theme.palette.smartSkillTalk.linkBlue,
                opacity: 0.9
              }
            }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー編集/作成ダイアログ */}
      <UserFormDialog
        open={openDialog}
        initialUser={selectedUser}
        currentUserDepartment={currentUser?.department}
        currentUserDepartments={
          currentUser?.userRole === 'SYSTEM_ADMIN'
            ? undefined
            : (() => {
                const depts = currentUser?.departments;
                if (depts && depts.length > 0) return depts;
                return currentUser?.department ? [currentUser.department] : [];
              })()
        }
        canSelectProfile={canAccess(loggedInUser, 'profileMgmt')}
        callerProfileId={loggedInUser?.profileId}
        callerManagementScope={
          currentUser?.userRole === 'SYSTEM_ADMIN'
            ? undefined
            : (currentUser?.managementScope || undefined)
        }
        profiles={profiles}
        departmentOptions={Array.from(
          new Set(
            users.flatMap((u) => [
              u.department,
              ...(u.departments ?? []),
            ]).filter(Boolean) as string[]
          )
        )}
        onClose={handleCloseDialog}
        onSave={handleSaveUserFromDialog}
      />

      {/* 一括削除確認ダイアログ */}
      <Dialog
        open={openBulkDeleteDialog}
        disableEscapeKeyDown={bulkDeleteLoading}
        onClose={(event, reason) => {
          if (bulkDeleteLoading) return;
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseBulkDeleteDialog();
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#FF4D4D', 
          color: 'white',
          fontSize: '18px',
          fontWeight: 400
        }}>
          一括削除の確認
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            選択した {selectedUsers.length} 人のユーザーを削除してもよろしいですか？<br/>
            この操作は元に戻せません。
          </DialogContentText>
          {dialogErrorMessage && (
            <Alert 
              severity="error" 
              sx={{ mt: 2 }}
              onClose={() => !bulkDeleteLoading && setDialogErrorMessage(null)}
            >
              {dialogErrorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleBulkDelete} 
            variant="contained"
            disabled={bulkDeleteLoading}
            startIcon={bulkDeleteLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{ 
              bgcolor: '#FF4D4D',
              color: 'white',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: '#FF4D4D',
                opacity: 0.9
              }
            }}
          >
            {bulkDeleteLoading ? '削除中…' : '削除'}
          </Button>
          <Button 
            onClick={handleCloseBulkDeleteDialog}
            disabled={bulkDeleteLoading}
            sx={{ 
              color: '#666666',
              borderColor: '#666666',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#666666',
                opacity: 0.9
              }
            }}
            variant="outlined"
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ（単一ユーザー） */}
      <Dialog
        open={openDeleteDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseDeleteDialog();
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#FF4D4D', 
          color: 'white',
          fontSize: '18px',
          fontWeight: 400
        }}>
          ユーザーの削除
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText>
            {selectedUser?.displayName} ({selectedUser?.givenUserId}) を削除してもよろしいですか？<br/>
            この操作は元に戻せません。
          </DialogContentText>
          {/* ダイアログ内エラーメッセージ表示 */}
          {dialogErrorMessage && (
            <Alert 
              severity="error" 
              sx={{ mt: 2 }}
              onClose={() => setDialogErrorMessage(null)}
            >
              {dialogErrorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDeleteUser} 
            variant="contained"
            sx={{ 
              bgcolor: '#FF4D4D',
              color: 'white',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: '#FF4D4D',
                opacity: 0.9
              }
            }}
          >
            削除
          </Button>
          <Button 
            onClick={handleCloseDeleteDialog}
            sx={{ 
              color: '#666666',
              borderColor: '#666666',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#666666',
                opacity: 0.9
              }
            }}
            variant="outlined"
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* パスワード変更ダイアログ */}
      <Dialog
        open={openPasswordDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleClosePasswordDialog();
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '10px',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: theme.palette.smartSkillTalk.linkBlue, 
          color: 'white',
          fontSize: '18px',
          fontWeight: 400
        }}>
          パスワード変更
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            {selectedUser?.displayName} ({selectedUser?.givenUserId}) のパスワードを変更します。
          </DialogContentText>
          {/* ダイアログ内エラーメッセージ表示 */}
          {dialogErrorMessage && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setDialogErrorMessage(null)}
            >
              {dialogErrorMessage}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <PasswordFieldWithSideToggle
              margin="dense"
              density="dense"
              id="newPassword"
              name="newPassword"
              label="新しいパスワード"
              fullWidth
              value={passwordData.newPassword}
              onChange={handlePasswordInputChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '3px',
                  '& fieldset': {
                    borderColor: '#898E93',
                  },
                },
              }}
              required
              helperText={getPasswordPolicyDescription()}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <PasswordFieldWithSideToggle
              margin="dense"
              density="dense"
              id="confirmPassword"
              name="confirmPassword"
              label="パスワード（確認）"
              fullWidth
              value={passwordData.confirmPassword}
              onChange={handlePasswordInputChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '3px',
                  '& fieldset': {
                    borderColor: '#898E93',
                  },
                },
              }}
              required
            />
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={passwordData.forceChange}
                onChange={handleForceChangeToggle}
                sx={{ 
                  color: theme.palette.smartSkillTalk.linkBlue,
                  '&.Mui-checked': {
                    color: theme.palette.smartSkillTalk.linkBlue,
                  }
                }}
              />
              <span>次回ログイン時にパスワード変更を強制する</span>
            </label>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleResetPassword} 
            variant="contained"
            disabled={isPasswordChangeDisabled()}
            sx={{ 
              bgcolor: theme.palette.smartSkillTalk.linkBlue,
              color: 'white',
              borderRadius: '6px',
              '&:hover': {
                bgcolor: theme.palette.smartSkillTalk.linkBlue,
                opacity: 0.9
              }
            }}
          >
            パスワード変更
          </Button>
          <Button 
            onClick={handleClosePasswordDialog}
            sx={{ 
              color: theme.palette.smartSkillTalk.linkBlue,
              borderColor: theme.palette.smartSkillTalk.linkBlue,
              borderRadius: '6px',
              '&:hover': {
                borderColor: theme.palette.smartSkillTalk.linkBlue,
                opacity: 0.9
              }
            }}
            variant="outlined"
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
